import type {
  CaseStepRecord,
  Employee,
  HrCase,
  HrEventMap,
  Playbook,
  PolicyVerdict,
  VentureId,
} from '@squadlab/contracts';
import { HR_EVENTS } from '@squadlab/contracts';
import type { CaseRepository } from '../ports/case-repository.js';
import type { SopAgent } from '../ports/sop-agent.js';
import type { Clock } from '../ports/clock.js';
import type { IdProvider } from '../ports/id.js';
import type { EventBus } from './event-bus.js';
import { err, ok, type Result } from './result.js';

/**
 * The generic case runner (ADR 0052): walks a playbook's steps, calling the
 * SopAgent for drafts and registered policy checks for verdicts, pausing at
 * human gates, and appending EVERYTHING to the case's audit trail — history is
 * never rewritten (a redo appends a fresh draft + a fresh gate).
 *
 * Domain-significant outcomes publish versioned `hr.*.v1` events with a
 * correlation id from the injected id provider and time from the injected
 * clock. New HR functions reuse this runner unchanged — they are playbook
 * content plus policy checks.
 */

/** Free-form request parameters captured at open (dates, reasons, …). */
export type CaseInput = Readonly<Record<string, string>>;

/** A policy check the runner can execute, registered per playbook step id. */
export type PolicyCheck = (subject: { readonly employee?: Employee; readonly input?: CaseInput }) => PolicyVerdict;

/**
 * A record-update effect (balance deduction, record activation …), registered
 * per playbook step id. Runs post-gate; reads its facts from the case's audit
 * trail (verdict `computed` figures) so approvals replay honestly.
 */
export type RecordUpdate = (subject: { readonly hrCase: HrCase; readonly employee?: Employee }) => void;

export interface CaseRunnerDeps {
  readonly cases: CaseRepository;
  readonly agent: SopAgent;
  readonly clock: Clock;
  readonly id: IdProvider;
  readonly events: EventBus<HrEventMap>;
  /** `"<playbookKey>/<stepId>"` → check. */
  readonly policyChecks: Readonly<Record<string, PolicyCheck>>;
  /** `"<playbookKey>/<stepId>"` → record-update effect. */
  readonly recordUpdates?: Readonly<Record<string, RecordUpdate>>;
  /** Resolve a case's subject employee (drafting context on redo, hooks on approve). */
  readonly resolveEmployee?: (
    ventureId: VentureId,
    employeeId: string,
    signal: AbortSignal,
  ) => Promise<Employee | undefined>;
}

export interface OpenCaseInput {
  readonly playbook: Playbook;
  readonly ventureId: VentureId;
  readonly employee?: Employee;
  /** Request parameters recorded into checks/drafts (e.g. leave dates). */
  readonly input?: CaseInput;
}

export interface CaseRunner {
  /** Open a case and advance it until a gate or completion. */
  open(input: OpenCaseInput, signal: AbortSignal): Promise<Result<HrCase>>;
  /** Approve the pending gate and advance to the next gate or completion. */
  approve(ventureId: VentureId, caseId: string, note: string | undefined, signal: AbortSignal): Promise<Result<HrCase>>;
  /** Request a redo: re-draft the nearest preceding agent step, then re-gate. */
  redo(ventureId: VentureId, caseId: string, note: string | undefined, signal: AbortSignal): Promise<Result<HrCase>>;
  /** Reject at the gate — the case closes as rejected. */
  reject(ventureId: VentureId, caseId: string, note: string | undefined, signal: AbortSignal): Promise<Result<HrCase>>;
}

export function createCaseRunner(deps: CaseRunnerDeps): CaseRunner {
  const { cases, agent, clock, id, events, policyChecks, recordUpdates = {}, resolveEmployee } = deps;

  const envelope = (): { correlationId: string; occurredAt: number } => ({
    correlationId: id.next(),
    occurredAt: clock.now(),
  });

  const recordStep = (hrCase: HrCase, step: CaseStepRecord): HrCase => {
    const next: HrCase = { ...hrCase, steps: [...hrCase.steps, step] };
    events.publish(HR_EVENTS.caseStepRecorded, {
      ...envelope(),
      caseId: next.id,
      ventureId: next.ventureId,
      stepId: step.stepId,
      status: step.status,
    });
    return next;
  };

  const close = (hrCase: HrCase, outcome: 'completed' | 'rejected' | 'cancelled'): HrCase => {
    const status = outcome === 'completed' ? 'completed' : outcome === 'rejected' ? 'rejected' : 'cancelled';
    const next: HrCase = { ...hrCase, status, closedAt: clock.now() };
    events.publish(HR_EVENTS.caseClosed, {
      ...envelope(),
      caseId: next.id,
      ventureId: next.ventureId,
      outcome,
    });
    return next;
  };

  /** Index of the next playbook step that has no record yet (redos re-count). */
  const doneCount = (hrCase: HrCase, playbook: Playbook): number => {
    // A step is settled when its LATEST record is terminal for that step kind.
    let settled = 0;
    for (const step of playbook.steps) {
      const records = hrCase.steps.filter((r) => r.stepId === step.id);
      const latest = records[records.length - 1];
      if (!latest) break;
      if (step.kind === 'human-gate' && (latest.status === 'awaiting-approval' || latest.status === 'redo-requested')) {
        break; // gate not resolved yet
      }
      settled++;
    }
    return settled;
  };

  /** Advance from the first unsettled step until a gate pauses us or we finish. */
  const advance = async (
    hrCase: HrCase,
    playbook: Playbook,
    employee: Employee | undefined,
    signal: AbortSignal,
    input?: CaseInput,
  ): Promise<Result<HrCase>> => {
    let current = hrCase;
    for (let i = doneCount(current, playbook); i < playbook.steps.length; i++) {
      if (signal.aborted) return err('cancelled', 'Case advance was cancelled');
      const step = playbook.steps[i];
      if (!step) break;

      if (step.kind === 'agent-draft') {
        const draft = await agent.draft(
          { playbook, step, ...(employee ? { employee } : {}), ...(input ? { input } : {}) },
          signal,
        );
        if (!draft.ok) return draft;
        current = recordStep(current, {
          stepId: step.id,
          kind: step.kind,
          status: 'done',
          actor: 'agent',
          at: clock.now(),
          artifact: draft.value,
        });
      } else if (step.kind === 'policy-check') {
        const check = policyChecks[`${playbook.key}/${step.id}`];
        const verdict: PolicyVerdict = check
          ? check({ ...(employee ? { employee } : {}), ...(input ? { input } : {}) })
          : {
              allowed: true,
              ruleRef: 'n/a',
              explanation: 'No policy check registered for this step.',
            };
        current = recordStep(current, {
          stepId: step.id,
          kind: step.kind,
          status: 'done',
          actor: 'system',
          at: clock.now(),
          verdict,
        });
      } else if (step.kind === 'human-gate') {
        current = recordStep(current, {
          stepId: step.id,
          kind: step.kind,
          status: 'awaiting-approval',
          actor: 'system',
          at: clock.now(),
        });
        current = { ...current, status: 'awaiting-gate' };
        return cases.save(current, signal);
      } else {
        // record-update: run the registered effect (balance deduction etc.)
        // BEFORE recording the trail row; a throwing effect degrades to a
        // Result so the failure surfaces instead of a half-recorded step.
        const update = recordUpdates[`${playbook.key}/${step.id}`];
        if (update) {
          try {
            update({ hrCase: current, ...(employee ? { employee } : {}) });
          } catch (cause) {
            return err('unknown', `Record update ${step.id} failed`, cause);
          }
        }
        current = recordStep(current, {
          stepId: step.id,
          kind: step.kind,
          status: 'done',
          actor: 'system',
          at: clock.now(),
        });
      }
    }
    current = close(current, 'completed');
    return cases.save(current, signal);
  };

  /** Find the case + its pending gate, or explain why not. */
  const pendingGate = (hrCase: HrCase): CaseStepRecord | undefined => {
    const latest = hrCase.steps[hrCase.steps.length - 1];
    return latest && latest.kind === 'human-gate' && latest.status === 'awaiting-approval' ? latest : undefined;
  };

  const requirePlaybook = (hrCase: HrCase): Result<Playbook> => {
    // The runner needs the playbook the case pinned. Resolved via the registry
    // injected at the composition root (kept in deps to avoid a cycle).
    const pb = registry.get(`${hrCase.playbookKey}@${hrCase.playbookVersion}`);
    return pb ? ok(pb) : err('not_found', `Playbook ${hrCase.playbookKey} v${hrCase.playbookVersion} is not registered`);
  };

  // Local playbook registry, filled by register(); the composition root
  // registers every playbook it offers so gate actions can resume any case.
  const registry = new Map<string, Playbook>();

  const runner: CaseRunner & { register(playbook: Playbook): void } = {
    register(playbook) {
      registry.set(`${playbook.key}@${playbook.version}`, playbook);
    },

    async open({ playbook, ventureId, employee, input }, signal) {
      runner.register(playbook);
      const hrCase: HrCase = {
        id: id.next(),
        ventureId,
        ...(employee ? { employeeId: employee.id } : {}),
        playbookKey: playbook.key,
        playbookVersion: playbook.version,
        status: 'open',
        steps: [],
        openedAt: clock.now(),
      };
      const saved = await cases.save(hrCase, signal);
      if (!saved.ok) return saved;
      events.publish(HR_EVENTS.caseOpened, {
        ...envelope(),
        caseId: hrCase.id,
        ventureId,
        playbookKey: playbook.key,
        playbookVersion: playbook.version,
        ...(employee ? { employeeId: employee.id } : {}),
      });
      return advance(saved.value, playbook, employee, signal, input);
    },

    async approve(ventureId, caseId, note, signal) {
      const found = await cases.get(ventureId, caseId, signal);
      if (!found.ok) return found;
      const gate = pendingGate(found.value);
      if (!gate) return err('validation', 'This case has no gate awaiting approval');
      const pb = requirePlaybook(found.value);
      if (!pb.ok) return pb;

      let current = recordStep(found.value, {
        stepId: gate.stepId,
        kind: 'human-gate',
        status: 'approved',
        actor: 'operator',
        at: clock.now(),
        ...(note ? { note } : {}),
      });
      current = { ...current, status: 'open' };
      const employee = await subjectOf(current, signal);
      return advance(current, pb.value, employee, signal);
    },

    async redo(ventureId, caseId, note, signal) {
      const found = await cases.get(ventureId, caseId, signal);
      if (!found.ok) return found;
      const gate = pendingGate(found.value);
      if (!gate) return err('validation', 'This case has no gate awaiting approval');
      const pb = requirePlaybook(found.value);
      if (!pb.ok) return pb;

      let current = recordStep(found.value, {
        stepId: gate.stepId,
        kind: 'human-gate',
        status: 'redo-requested',
        actor: 'operator',
        at: clock.now(),
        ...(note ? { note } : {}),
      });

      // Re-draft the nearest preceding agent step, then re-open the same gate.
      const gateIndex = pb.value.steps.findIndex((s) => s.id === gate.stepId);
      const draftStep = [...pb.value.steps.slice(0, gateIndex)].reverse().find((s) => s.kind === 'agent-draft');
      const employee = await subjectOf(current, signal);
      if (draftStep) {
        const draft = await agent.draft(
          { playbook: pb.value, step: draftStep, ...(employee ? { employee } : {}) },
          signal,
        );
        if (!draft.ok) return draft;
        current = recordStep(current, {
          stepId: draftStep.id,
          kind: 'agent-draft',
          status: 'done',
          actor: 'agent',
          at: clock.now(),
          artifact: draft.value,
        });
      }
      current = recordStep(current, {
        stepId: gate.stepId,
        kind: 'human-gate',
        status: 'awaiting-approval',
        actor: 'system',
        at: clock.now(),
      });
      current = { ...current, status: 'awaiting-gate' };
      return cases.save(current, signal);
    },

    async reject(ventureId, caseId, note, signal) {
      const found = await cases.get(ventureId, caseId, signal);
      if (!found.ok) return found;
      const gate = pendingGate(found.value);
      if (!gate) return err('validation', 'This case has no gate awaiting approval');

      let current = recordStep(found.value, {
        stepId: gate.stepId,
        kind: 'human-gate',
        status: 'rejected',
        actor: 'operator',
        at: clock.now(),
        ...(note ? { note } : {}),
      });
      current = close(current, 'rejected');
      return cases.save(current, signal);
    },
  };

  /** Resolve the case's subject employee (redo drafting context, update hooks). */
  const subjectOf = async (hrCase: HrCase, signal: AbortSignal): Promise<Employee | undefined> => {
    if (!hrCase.employeeId || !resolveEmployee) return undefined;
    return resolveEmployee(hrCase.ventureId, hrCase.employeeId, signal);
  };

  return runner;
}
